import { useMemo, type ReactNode } from 'react';
import { Plus, Search } from 'lucide-react';

import { Hint } from '../../../chrome/hint';
import { useViewSearch } from '../../../chrome/viewSearch';
import { getLocale } from '../../../i18n';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '../../../ui/empty';
import { Input } from '../../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Skeleton } from '../../../ui/skeleton';
import { tagIconName } from '../../tenant/tags/icons';
import { AccountCard } from './AccountCard';
import { useTagCategories, useTrackedAccounts } from './api/queries';
import type { TagPair, TrackedAccountRow } from './api/schemas';
import { am } from './messages';
import { TagField } from './TagField';

export interface AccountsProps {
  campaignId: string;
  onSelectAccount?: (accountId: string) => void;
  onEditAccount?: (accountId: string) => void;
  isAnalyst?: boolean;
  onAddAccount?: () => void;
}

/** Values within one category widen the match; categories narrow it. */
function matchesFilter(account: TrackedAccountRow, pairs: TagPair[]): boolean {
  const byCategory = new Map<string, TagPair[]>();
  for (const pair of pairs) {
    byCategory.set(pair.category, [...(byCategory.get(pair.category) ?? []), pair]);
  }
  return [...byCategory.values()].every((group) =>
    group.some(
      (pair) =>
        (account.account_tags ?? []).some((tag) => tag.value_id === pair.valueId) ||
        account.tags.includes(pair.value),
    ),
  );
}

export function Accounts({
  campaignId,
  onSelectAccount,
  onEditAccount,
  isAnalyst = false,
  onAddAccount,
}: AccountsProps): ReactNode {
  const locale = getLocale();
  const [filters, setFilters] = useViewSearch(`accounts.${campaignId}`, {
    q: { default: '' },
    tags: { default: [], list: true },
    platform: { default: 'all' },
  });
  const searchQuery = filters.q;
  const setSearchQuery = (q: string): void => setFilters({ q });
  const selectedPlatform = filters.platform;
  const setSelectedPlatform = (platform: string): void => setFilters({ platform });

  const query = useTrackedAccounts(campaignId);
  const vocabulary = useTagCategories(campaignId, true);
  const accounts = query.data ?? [];
  const pairs = useMemo(
    (): TagPair[] =>
      filters.tags.map((name): TagPair => {
        for (const category of vocabulary.data ?? []) {
          const value = category.values.find((candidate) => candidate.name === name);
          if (value) {
            return {
              category: category.name,
              value: value.name,
              valueId: value.id,
              icon: tagIconName(value, category),
            };
          }
        }
        return { category: '', value: name, valueId: `tag:${name}` };
      }),
    [filters.tags, vocabulary.data],
  );

  const setPairs = (next: TagPair[]): void => setFilters({ tags: next.map((pair) => pair.value) });

  const filteredAccounts = accounts.filter((account) => {
    if (selectedPlatform !== 'all' && account.platform !== selectedPlatform) {
      return false;
    }
    if (
      searchQuery.trim() !== '' &&
      !(account.handle ?? '').toLowerCase().includes(searchQuery.toLowerCase().trim())
    ) {
      return false;
    }
    return matchesFilter(account, pairs);
  });

  return (
    <div className="space-y-4 w-full">
      <h1 className="sr-only">{am('title')}</h1>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
        <div className="relative w-full lg:max-w-xs">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none"
            aria-hidden="true"
          />
          <Input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={am('search.placeholder')}
            aria-label={am('search.label')}
            className="pl-9 h-9 w-full"
          />
        </div>

        <div className="min-w-0 flex-1">
          <TagField campaignId={campaignId} value={pairs} onChange={setPairs} mode="filter" />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
            <SelectTrigger className="h-9" aria-label={am('filter.platform')}>
              <SelectValue placeholder={am('filter.platform')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{am('filter.allPlatforms')}</SelectItem>
              <SelectItem value="instagram">{am('platform.instagram')}</SelectItem>
              <SelectItem value="tiktok">{am('platform.tiktok')}</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1">
            {!isAnalyst && (
              <Button onClick={onAddAccount} className="h-9">
                <Plus className="mr-1.5 size-4" aria-hidden="true" />
                {am('add.account')}
              </Button>
            )}
            <Hint label={am('hint.profiles.label')} description={am('hint.profiles.alignment')}>
              {am('hint.profiles.what')}
            </Hint>
          </div>
        </div>
      </div>

      {query.isLoading ? (
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Skeleton className="size-4 rounded-full" />
            <span>{am('loading')}</span>
          </div>
          <div
            aria-hidden
            className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
          >
            {[0, 1, 2].map((slot) => (
              <Card key={slot} className="gap-3 px-4 py-4">
                <div className="flex items-center gap-3">
                  <Skeleton className="size-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </div>
                <Skeleton className="h-5 w-1/2" />
                <Skeleton className="h-4 w-full" />
              </Card>
            ))}
          </div>
        </div>
      ) : query.isError ? (
        <Card className="w-full border-destructive/50 bg-destructive/5">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-destructive">
              {am('error.title')}
            </CardTitle>
            <CardDescription className="text-destructive/80">
              {am('error.description')}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : filteredAccounts.length === 0 ? (
        <Card className="border-border">
          <CardContent className="py-12">
            <Empty>
              <EmptyHeader>
                <EmptyTitle>{am('empty.title')}</EmptyTitle>
                <EmptyDescription>{am('empty.description')}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          </CardContent>
        </Card>
      ) : (
        <ul
          aria-label={am('title')}
          className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
        >
          {filteredAccounts.map((account) => (
            <li key={account.id}>
              <AccountCard
                campaignId={campaignId}
                account={account}
                locale={locale}
                canWrite={!isAnalyst}
                onOpen={onSelectAccount}
                onEdit={onEditAccount}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
